import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CalendarioView = ({ eventos, onEventClick, viewMode, onViewModeChange }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)?.getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)?.getDay();
  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const getEventosForDay = (day) => {
    const dateStr = `${currentDate?.getFullYear()}-${String(currentDate?.getMonth() + 1)?.padStart(2, '0')}-${String(day)?.padStart(2, '0')}`;
    return eventos?.filter(e => e?.fecha === dateStr) || [];
  };

  const getEstadoColor = (estado) => {
    const colors = {
      PROGRAMADO: 'bg-warning',
      REALIZADO: 'bg-success',
      CANCELADO: 'bg-error'
    };
    return colors?.[estado] || 'bg-muted';
  };

  const getTipoServicioColor = (tipo) => {
    const colors = {
      EVENTO: 'bg-primary',
      TIENDA: 'bg-secondary',
      FERIA: 'bg-warning',
      PRIVADO: 'bg-success'
    };
    return colors?.[tipo] || 'bg-muted';
  };

  const renderMonthlyView = () => (
    <div className="grid grid-cols-7 gap-1 md:gap-2">
      {dayNames?.map(day => (
        <div key={day} className="text-center text-xs md:text-sm font-caption font-medium text-muted-foreground py-2">
          {day}
        </div>
      ))}
      
      {Array.from({ length: firstDayOfMonth })?.map((_, index) => (
        <div key={`empty-${index}`} className="aspect-square" />
      ))}
      
      {Array.from({ length: daysInMonth })?.map((_, index) => {
        const day = index + 1;
        const eventosDay = getEventosForDay(day);
        const isToday = day === new Date()?.getDate() && 
                       currentDate?.getMonth() === new Date()?.getMonth() && 
                       currentDate?.getFullYear() === new Date()?.getFullYear();

        return (
          <div
            key={day}
            className={`
              aspect-square p-1 md:p-2 rounded-lg border transition-smooth hover:border-primary cursor-pointer
              ${isToday ? 'border-primary bg-primary/5' : 'border-border'}
              ${eventosDay?.length > 0 ? 'bg-muted/30' : ''}
            `}
          >
            <div className="flex flex-col h-full">
              <span className={`text-xs md:text-sm font-caption ${isToday ? 'font-semibold text-primary' : 'text-foreground'}`}>
                {day}
              </span>
              {eventosDay?.length > 0 && (
                <div className="flex-1 flex flex-col gap-0.5 mt-1 overflow-hidden">
                  {eventosDay?.slice(0, 3)?.map((evento, idx) => (
                    <div
                      key={idx}
                      className={`text-[8px] md:text-[10px] px-1 py-0.5 rounded ${getTipoServicioColor(evento?.tipoServicio)} text-white truncate cursor-pointer hover:opacity-80`}
                      onClick={() => onEventClick(evento)}
                      title={`${evento?.cliente} – ${evento?.tipoServicio}`}
                    >
                      {evento?.cliente} – {evento?.tipoServicio}
                    </div>
                  ))}
                  {eventosDay?.length > 3 && (
                    <span className="text-[8px] md:text-[10px] text-muted-foreground">
                      +{eventosDay?.length - 3} más
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const getWeekDates = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek?.setDate(currentDate?.getDate() - currentDate?.getDay());
    
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(startOfWeek);
      date?.setDate(startOfWeek?.getDate() + i);
      return date;
    });
  };

  const previousWeek = () => {
    const newDate = new Date(currentDate);
    newDate?.setDate(currentDate?.getDate() - 7);
    setCurrentDate(newDate);
  };

  const nextWeek = () => {
    const newDate = new Date(currentDate);
    newDate?.setDate(currentDate?.getDate() + 7);
    setCurrentDate(newDate);
  };

  const renderWeeklyView = () => {
    const weekDates = getWeekDates();
    
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-7 gap-2">
          {weekDates?.map((date, idx) => {
            const dateStr = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}-${String(date?.getDate())?.padStart(2, '0')}`;
            const eventosDay = eventos?.filter(e => e?.fecha === dateStr) || [];
            const isToday = date?.toDateString() === new Date()?.toDateString();

            return (
              <div key={idx} className={`border rounded-lg p-2 ${isToday ? 'border-primary bg-primary/5' : 'border-border'}`}>
                <div className="text-center mb-2">
                  <div className="text-xs text-muted-foreground">{dayNames?.[idx]}</div>
                  <div className={`text-sm font-semibold ${isToday ? 'text-primary' : 'text-foreground'}`}>
                    {date?.getDate()}
                  </div>
                </div>
                <div className="space-y-1">
                  {eventosDay?.map((evento, eventIdx) => (
                    <div
                      key={eventIdx}
                      className={`text-xs p-1.5 rounded ${getTipoServicioColor(evento?.tipoServicio)} text-white cursor-pointer hover:opacity-80`}
                      onClick={() => onEventClick(evento)}
                    >
                      <div className="font-semibold truncate">{evento?.cliente}</div>
                      <div className="text-[10px] truncate">{evento?.tipoServicio}</div>
                      {evento?.horaInicio && (
                        <div className="text-[10px]">{evento?.horaInicio}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 shadow-sm border border-border">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-4 md:mb-6 gap-4">
        <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
          Calendario de Eventos
        </h2>
        
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-muted rounded-lg p-1">
            <Button
              size="sm"
              variant={viewMode === 'monthly' ? 'default' : 'ghost'}
              onClick={() => onViewModeChange('monthly')}
            >
              Mensual
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'weekly' ? 'default' : 'ghost'}
              onClick={() => onViewModeChange('weekly')}
            >
              Semanal
            </Button>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={viewMode === 'monthly' ? previousMonth : previousWeek}
          className="p-1.5 md:p-2 rounded-md hover:bg-muted transition-smooth active-press"
        >
          <Icon name="ChevronLeft" size={18} className="md:w-5 md:h-5" />
        </button>
        <span className="text-sm md:text-base font-caption font-medium text-foreground min-w-[120px] md:min-w-[140px] text-center">
          {viewMode === 'monthly' 
            ? `${monthNames?.[currentDate?.getMonth()]} ${currentDate?.getFullYear()}`
            : `Semana del ${getWeekDates()?.[0]?.getDate()} ${monthNames?.[getWeekDates()?.[0]?.getMonth()]}`
          }
        </span>
        <button
          onClick={viewMode === 'monthly' ? nextMonth : nextWeek}
          className="p-1.5 md:p-2 rounded-md hover:bg-muted transition-smooth active-press"
        >
          <Icon name="ChevronRight" size={18} className="md:w-5 md:h-5" />
        </button>
      </div>
      {viewMode === 'monthly' ? renderMonthlyView() : renderWeeklyView()}
      <div className="mt-4 md:mt-6 flex flex-wrap gap-3 md:gap-4 text-xs md:text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-primary" />
          <span className="font-caption text-muted-foreground">Evento</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-secondary" />
          <span className="font-caption text-muted-foreground">Tienda</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-warning" />
          <span className="font-caption text-muted-foreground">Feria</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-success" />
          <span className="font-caption text-muted-foreground">Privado</span>
        </div>
      </div>
    </div>
  );
};

export default CalendarioView;